import { Users, 
  Fuel, 
  Camera, 
  Tent, 
  MapPin, 
  Tag,
  Droplet,
  Mountain,
  Utensils,
  Eye,
  type LucideIcon } from '@/ui/icons'

export type POIType = 'gas' | 'water' | 'viewpoint' | 'campsite' | 'meetup_spot' | 'trailhead' | 'custom' | null | undefined

export interface WaypointTypeStyle {
  bgColor: string
  borderColor: string
  indicatorColor: string
  textColor: string
  icon: LucideIcon
  label: string
}

/**
 * Determine if a waypoint is a main itinerary stop (Route Stop)
 * Route stops include: null poi_type, campsites, meetup spots, and fuel stops
 * These are the primary destinations for overlanding/camping trips
 */
export function isRouteStop(poiType: POIType): boolean {
  if (poiType === null || poiType === undefined) {
    return true // Default route stop
  }
  // Campsites, meetup spots, and fuel stops are main itinerary stops
  if (poiType === 'campsite' || poiType === 'meetup_spot' || poiType === 'gas') {
    return true
  }
  return false
}

/**
 * Determine if a waypoint is a detour (not a main itinerary stop)
 * Detours are side trips like photo spots, hikes, lunch stops, etc.
 */
export function isDetour(poiType: POIType): boolean {
  return !isRouteStop(poiType)
}

/**
 * Tag variant for itinerary list design: gold (adventure) vs dark gray (earth).
 * - Adventure: meetup_spot and all detour types (viewpoint, gas, water, custom, etc.)
 * - Earth: trailhead, campsite, and default/waypoint (null)
 */
export function getWaypointTagVariant(poiType: POIType, isDetour: boolean): 'adventure' | 'earth' {
  if (isDetour) return 'adventure'
  if (poiType === 'meetup_spot') return 'adventure'
  return 'earth'
}

/**
 * Get the style configuration for a waypoint type
 */
export function getWaypointTypeStyle(poiType: POIType): WaypointTypeStyle {
  switch (poiType) {
    case 'meetup_spot':
      // Meet Here - main itinerary stop, using adventure blue like route stops
      return {
        bgColor: 'bg-adventure-100',
        borderColor: 'border-adventure-300',
        indicatorColor: 'bg-adventure-500',
        textColor: 'text-adventure-700',
        icon: Users,
        label: 'Meet Here'
      }
    
    case 'gas':
      // Fuel stop - using earth tones for practical/grounded feel
      return {
        bgColor: 'bg-earth-200',
        borderColor: 'border-earth-400',
        indicatorColor: 'bg-earth-500',
        textColor: 'text-earth-700',
        icon: Fuel,
        label: 'Fuel'
      }
    
    case 'water':
      // Water - using adventure blue for refreshment/flow
      return {
        bgColor: 'bg-adventure-200',
        borderColor: 'border-adventure-400',
        indicatorColor: 'bg-adventure-500',
        textColor: 'text-adventure-700',
        icon: Droplet,
        label: 'water'
      }
    
    case 'viewpoint':
      // Photo Spot - using lighter adventure blue for scenic/expansive feel
      return {
        bgColor: 'bg-adventure-100',
        borderColor: 'border-adventure-300',
        indicatorColor: 'bg-adventure-400',
        textColor: 'text-adventure-700',
        icon: Camera,
        label: 'Photo Spot'
      }
    
    case 'campsite':
      // Campsite - main itinerary stop, using adventure blue like route stops
      return {
        bgColor: 'bg-adventure-100',
        borderColor: 'border-adventure-300',
        indicatorColor: 'bg-adventure-500',
        textColor: 'text-adventure-700',
        icon: Tent,
        label: 'Campsite'
      }
    
    case 'trailhead':
      // Hike - using deeper earth tones for trail/outdoor activity
      return {
        bgColor: 'bg-earth-100',
        borderColor: 'border-earth-400',
        indicatorColor: 'bg-earth-700',
        textColor: 'text-earth-800',
        icon: Mountain,
        label: 'Hike'
      }
    
    case 'custom':
      // Custom type can be Lunch Stop, Overlook, or other custom labels
      // Default styling for custom (will be overridden by customLabel in getWaypointTypeLabel)
      // Using earth tones for flexibility
      return {
        bgColor: 'bg-earth-100',
        borderColor: 'border-earth-300',
        indicatorColor: 'bg-earth-500',
        textColor: 'text-earth-800',
        icon: Utensils,
        label: 'Custom'
      }
    
    default:
      // Route Stop (null poi_type) - main itinerary waypoint
      // Using primary adventure blue for main route
      return {
        bgColor: 'bg-adventure-100',
        borderColor: 'border-adventure-300',
        indicatorColor: 'bg-adventure-500',
        textColor: 'text-adventure-700',
        icon: MapPin,
        label: 'Route Stop'
      }
  }
}

/**
 * Get the icon component for a waypoint type
 */
export function getWaypointTypeIcon(poiType: POIType): LucideIcon {
  return getWaypointTypeStyle(poiType).icon
}

/**
 * Get the label text for a waypoint type
 */
export function getWaypointTypeLabel(poiType: POIType, customLabel?: string | null): string {
  if (poiType === 'custom' && customLabel) {
    // Use custom label as-is (Lunch Stop, Overlook, etc.)
    return customLabel
  }
  return getWaypointTypeStyle(poiType).label
}

/**
 * Get the icon for a waypoint type, considering custom labels
 */
export function getWaypointTypeIconWithCustom(poiType: POIType, customLabel?: string | null): LucideIcon {
  if (poiType === 'custom' && customLabel) {
    // Map custom labels to specific icons
    const labelLower = customLabel.toLowerCase()
    if (labelLower.includes('lunch')) {
      return Utensils
    }
    if (labelLower.includes('overlook')) {
      return Eye
    }
  }
  return getWaypointTypeStyle(poiType).icon
}

/**
 * Get custom styling for waypoint types with custom labels
 */
export function getCustomWaypointStyle(customLabel?: string | null): Partial<WaypointTypeStyle> | null {
  if (!customLabel) return null
  
  const labelLower = customLabel.toLowerCase()
  
  if (labelLower.includes('overlook')) {
    // Overlook - using adventure blue for scenic/expansive views
    return {
      bgColor: 'bg-adventure-100',
      borderColor: 'border-adventure-300',
      indicatorColor: 'bg-adventure-400',
      textColor: 'text-adventure-700',
      icon: Eye,
      label: 'Overlook'
    }
  }
  
  if (labelLower.includes('lunch')) {
    // Lunch Stop - using earth tones for warm, social meal break
    return {
      bgColor: 'bg-earth-100',
      borderColor: 'border-earth-300',
      indicatorColor: 'bg-earth-500',
      textColor: 'text-earth-800',
      icon: Utensils,
      label: 'Lunch Stop'
    }
  }
  
  return null
}

/**
 * Get display label for waypoint type dropdown
 * Maps POI types to user-friendly names
 */
export function getWaypointTypeDisplayLabel(poiType: POIType, customLabel?: string | null): string {
  if (poiType === null || poiType === undefined) {
    return 'Route Stop'
  }
  
  if (poiType === 'custom' && customLabel) {
    if (customLabel.toLowerCase().includes('lunch')) {
      return 'Lunch Stop'
    }
    return customLabel
  }
  
  const labelMap: Record<string, string> = {
    'viewpoint': 'Photo Spot',
    'trailhead': 'Hike',
    'campsite': 'Campsite',
    'meetup_spot': 'Meet Here',
    'gas': 'Fuel',
    'water': 'Water',
    'custom': 'Custom'
  }
  
  return labelMap[poiType] || poiType
}

